import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, Target, FileText, Zap, CheckCircle2, Clock, TrendingUp, ClipboardList, Sparkles } from "lucide-react";

export default function HomePage() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Stop planning. Start testing.
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Business plans are dead.{" "}
              <span className="text-primary">Test. Score. Grow.</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl">
              FoundrScore helps entrepreneurs and SME founders validate business ideas quickly, 
              generate a one-page business plan, and take immediate action to test demand.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quiz">
                <Button size="lg" className="rounded-full px-8 h-12 text-base shadow-lg shadow-primary/20 hover:scale-105 transition-transform" data-testid="hero-quick-test-btn">
                  Quick Test (5 min)
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/assessment">
                <Button variant="outline" size="lg" className="rounded-full px-8 h-12 text-base" data-testid="hero-full-assessment-btn">
                  Full Assessment
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-6 mt-10 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>5-15 min</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>Instant results</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span>Actionable insights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Choose Your Path to Validation
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From quick pulse check to comprehensive business plan — get the validation 
              level that matches your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Quick Test Card */}
            <Card className="group relative overflow-hidden border-2 border-transparent hover:border-primary/20 transition-all duration-300" data-testid="feature-quick-test">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <div className="text-xs font-semibold text-primary mb-2">5 MINUTES</div>
                <h3 className="text-xl font-bold mb-2">Quick Test</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  12 critical questions to get your Idea Readiness Score. Perfect for a fast pulse check.
                </p>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>RED / AMBER / GREEN score</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>Top risk flags</span>
                  </li>
                </ul>
                <Link to="/quiz">
                  <Button className="w-full rounded-full" data-testid="quick-test-cta">
                    Take Quick Test
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Full Assessment Card */}
            <Card className="group relative overflow-hidden border-2 border-primary/30 transition-all duration-300" data-testid="feature-full-assessment">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-bl-lg">
                RECOMMENDED
              </div>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                  <ClipboardList className="w-6 h-6 text-secondary" />
                </div>
                <div className="text-xs font-semibold text-secondary mb-2">15 MINUTES</div>
                <h3 className="text-xl font-bold mb-2">Full Assessment</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  8 comprehensive sections. Get your FoundrScore (0-100) + auto-generated business plan.
                </p>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary" />
                    <span>Detailed scoring by section</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary" />
                    <span>Auto-generated business plan</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary" />
                    <span>Branded PDF download</span>
                  </li>
                </ul>
                <Link to="/assessment">
                  <Button className="w-full rounded-full bg-secondary hover:bg-secondary/90" data-testid="full-assessment-cta">
                    Start Full Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Validation Canvas Card */}
            <Card className="group relative overflow-hidden border-2 border-transparent hover:border-primary/20 transition-all duration-300" data-testid="feature-canvas">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-xs font-semibold text-green-600 mb-2">MANUAL</div>
                <h3 className="text-xl font-bold mb-2">Validation Canvas</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Prefer to plan manually? Our structured canvas helps you map your idea step by step.
                </p>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>7 structured sections</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>30-day action plan</span>
                  </li>
                </ul>
                <Link to="/canvas">
                  <Button variant="outline" className="w-full rounded-full" data-testid="canvas-cta">
                    Create Canvas
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Idea to Action in Under 15 Minutes
            </h2>
            <p className="text-muted-foreground text-lg">
              From assessment to actionable plan — all in one session
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Take the Assessment",
                description: "Answer questions about your problem, solution, customers, and go-to-market."
              },
              {
                step: "02",
                title: "Get Your Score",
                description: "Receive your FoundrScore (0-100) with RED, AMBER, or GREEN rating."
              },
              {
                step: "03",
                title: "Generate Your Plan",
                description: "Auto-generate a one-page business plan from your answers."
              },
              {
                step: "04",
                title: "Take Action",
                description: "Download your PDF and start validating your idea immediately."
              }
            ].map((item, index) => (
              <div key={index} className="relative" data-testid={`how-it-works-step-${index + 1}`}>
                <div className="text-5xl font-bold text-muted/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to validate your idea?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of founders who've used FoundrScore to test their ideas 
            before investing time or money.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/assessment">
              <Button size="lg" variant="secondary" className="rounded-full px-8 h-12 text-base bg-white text-primary hover:bg-white/90" data-testid="cta-assessment-btn">
                Start Full Assessment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="https://calendly.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-white/30 text-white hover:bg-white/10" data-testid="cta-book-btn">
                Book Advisory Call
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
